/**
 * Module dependencies.
 */

import { Button, Image, Subtitle, Type } from 'src/components/core';
import { ReactElement, useMemo } from 'react';
import { awards } from 'src/core/content-config/awards';
import { color, media, units, useBreakpoint } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import AwardCard from 'src/components/cards/award-card';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import slice from 'lodash/slice';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'image image          image'
    '.     titleWrapper   .'
    '.     awardsTitle    .'
    '.     awardsSubtitle .'
    '.     awardsButton   .'
    '.     awards         .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      'image image          image          .      .      titleWrapper .'
      'image image          image          .      .      titleWrapper .'
      '.     awardsTitle    awardsTitle    .      .      .            .'
      '.     awardsSubtitle awardsSubtitle .      .      awardsButton .'
      '.     awards         awards         awards awards awards       .';
    grid-template-columns: 1fr 4fr repeat(2, 1fr) ${theme(
      'grid.columnsGutter'
    )}px 4fr 1fr;
    grid-template-rows: repeat(5, max-content);
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div`
  background-color: ${color('backgrounds.taupe01')};
  grid-column: 1 / -1;
  grid-row: 2 / -1;
  z-index: -1;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  overflow: hidden;
  padding-bottom: 69.9%;
  position: relative;
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-area: titleWrapper;
  margin-bottom: ${units(12.5)};
  padding-top: ${units(6)};

  ${media.min('md')`
    margin-bottom: 0;
    padding-top: ${units(3)};
  `}

  ${media.min('lg')`
    padding-top: ${units(6)};
  `}

  ${media.min('xxl')`
    padding-top: ${units(23.5)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(2)};

  ${media.min('md')`
    margin-bottom: ${units(2)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  margin-bottom: ${units(5)};

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `AwardsTitle` styled component.
 */

const AwardsTitle = styled(Type.H2)`
  grid-area: awardsTitle;
  padding-top: ${units(6)};

  ${media.min('md')`
    margin-bottom: ${units(1.5)};
    padding-top: ${units(17)};
  `}

  ${media.min('lg')`
    padding-top: ${units(25)};
  `}
`;

/**
 * `AwardsSubtitle` styled component.
 */

const AwardsSubtitle = styled(Subtitle)`
  grid-area: awardsSubtitle;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `AwardsButtonWrapper` styled component.
 */

const AwardsButtonWrapper = styled.div`
  grid-area: awardsButton;
  margin-bottom: ${units(5)};

  ${media.min('md')`
    align-self: center;
    justify-self: end;
    margin-bottom: 0;
  `}
`;

/**
 * `AwardsCardsWrapper` styled component.
 */

const AwardsCardsWrapper = styled.div`
  display: grid;
  grid-area: awards;
  grid-gap: ${units(3)} ${units(2.5)};
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: ${units(8)};

  ${media.min('ms')`
    grid-gap: ${units(9)} ${units(3)};
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    padding-top: ${units(9.5)};
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(4, 1fr);
    margin-bottom: ${units(18)};
  `}
`;

/**
 * `AwardsSection` component.
 */

const AwardsSection = (props: Props): ReactElement => {
  const { className } = props;
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const isMobile = useBreakpoint('ms', 'max');
  const isTablet = useBreakpoint('lg', 'max');
  const awardsCards = useMemo(() => {
    const totalCards = (isMobile && 2) || (isTablet && 3) || 4;

    return slice(awards, 0, totalCards);
  }, [isMobile, isTablet]);

  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <section className={className}>
      <StyledContainer>
        <Grid>
          <Background />

          <ImageWrapper
            data-scroll
            data-scroll-speed={isTablet ? 0 : 1}
          >
            <Image
              alt={translate('about-us:sections.innovation.title')}
              fill
              src={'/static/images/innovation.jpg'}
              style={{ objectFit: 'cover' }}
            />
          </ImageWrapper>

          <TitleWrapper>
            <Title {...animProps}>
              {translate('about-us:sections.innovation.title')}
            </Title>

            <Description {...animProps}>
              {translate('about-us:sections.innovation.description')}
            </Description>

            <div {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={routeResolver('innovation')}
              >
                {translate('common:actions.exploreOurPassion')}
              </Button>
            </div>
          </TitleWrapper>

          <AwardsTitle {...animProps}>
            {translate('about-us:sections.awards.title')}
          </AwardsTitle>

          <AwardsSubtitle {...animProps}>
            {translate('about-us:sections.awards.subtitle')}
          </AwardsSubtitle>

          <AwardsButtonWrapper {...animProps}>
            <Button
              colorTheme={'tertiary'}
              href={routeResolver('awards')}
              size={'small'}
            >
              {translate('common:actions.viewAll')}
            </Button>
          </AwardsButtonWrapper>

          <AwardsCardsWrapper>
            {map(awardsCards, ({ date, imageUrl, title }, index) => (
              <div
                data-scroll={'fade-in-up-small'}
                data-scroll-animation-delay={'150'}
                data-scroll-offset={'20%'}
                key={index}
              >
                <AwardCard
                  imageUrl={imageUrl}
                  label={date}
                  title={title}
                />
              </div>
            ))}
          </AwardsCardsWrapper>
        </Grid>
      </StyledContainer>
    </section>
  );
};

/**
 * Export `AwardsSection` component.
 */

export default AwardsSection;
